import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { getAllTemplates as getAllPredefined, getTemplateById as getPredefinedById } from '../templates/TemplateRegistry';
import { useLayout } from './LayoutContext';
import { useDesign } from './DesignContext';
import { useTypography } from './TypographyContext';
import { useStyle } from './StyleContext';
import { useBackground } from './BackgroundContext';
import { useSpecialEffect } from './SpecialEffectContext';
import { useBarStyle } from './BarStyleContext';

// Storage key
const SAVED_TEMPLATES_STORAGE_KEY = 'savedCustomTemplates';

// Create Context
const TemplateContext = createContext();

export const TemplateProvider = ({ children }) => {
  const { setSelectedLayout } = useLayout();
  const { setSelectedDesign, setFullCustomColorScheme } = useDesign();
  const { updateTypographySettings } = useTypography();
  const { setSelectedStyleId } = useStyle();
  const { setSelectedBackgroundId } = useBackground();
  const { setSelectedSpecialEffectId } = useSpecialEffect();
  const { setSelectedBarStyleId } = useBarStyle();

  // State for predefined templates
  const [predefinedTemplates, setPredefinedTemplates] = useState([]);
  // State for user-saved templates
  const [savedTemplates, setSavedTemplates] = useState([]);

  // Apply template function - moved before useEffect to avoid initialization error
  const applyTemplate = useCallback((template) => {
    if (!template || !template.settings) {
      console.error('TemplateContext: Invalid template object passed to applyTemplate', template);
      return;
    }

    console.log(`TemplateContext: Applying template: ${template.name}`);

    try {
      // Apply settings using setters from other contexts
      setSelectedLayout(template.settings.layoutId);
      setSelectedDesign(template.settings.designId);
      setSelectedStyleId(template.settings.style);
      setSelectedBackgroundId(template.settings.background);
      updateTypographySettings(template.settings.typography);
      setSelectedSpecialEffectId(template.settings.specialEffectId || 'none');
      setSelectedBarStyleId(template.settings.barStyleId || 'stars');

      // Handle custom color scheme application if present
      if (template.settings.designId === 'custom' && template.settings.customColors) {
        console.log('TemplateContext: Applying custom color scheme from template');
        setFullCustomColorScheme(template.settings.customColors);
        // Need to ensure selectedDesign is 'custom' if applying custom colors
        if (template.settings.designId !== 'custom') {
             console.warn("Template Warning: Applying customColors but template designId is not 'custom'. Setting designId to 'custom'.");
             setSelectedDesign('custom'); // Force design to custom
        }
      }
      
    } catch (error) {
      console.error(`TemplateContext: Error applying template settings for ${template.name}:`, error);
    }
  }, [
    setSelectedLayout,
    setSelectedDesign,
    updateTypographySettings,
    setFullCustomColorScheme,
    setSelectedStyleId,
    setSelectedBackgroundId,
    setSelectedSpecialEffectId,
    setSelectedBarStyleId,
  ]);

  // Load templates on mount
  useEffect(() => {
    // Load predefined templates
    try {
      setPredefinedTemplates(getAllPredefined());
    } catch (error) {
      console.error("Failed to load predefined templates:", error);
      setPredefinedTemplates([]);
    }

    // Load saved templates from localStorage
    try {
      const storedTemplates = localStorage.getItem(SAVED_TEMPLATES_STORAGE_KEY);
      if (storedTemplates) {
        setSavedTemplates(JSON.parse(storedTemplates));
        console.log('TemplateContext: Loaded saved templates');
      } else {
        setSavedTemplates([]);
      }
    } catch (error) {
      console.error("Failed to load saved templates from localStorage:", error);
      setSavedTemplates([]);
    }
  }, []);

  // Check for URL template parameter and apply it
  useEffect(() => {
    // Only run this after templates are loaded
    if (predefinedTemplates.length === 0) return;

    // Check for template parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = urlParams.get('template');
    
    if (templateId) {
      console.log('TemplateContext: Found template parameter in URL:', templateId);
      
      // Find the template by ID
      const template = getPredefinedById(templateId);
      
      if (template) {
        console.log('TemplateContext: Applying template from URL parameter:', template.name);
        applyTemplate(template);
        
        // Clean up URL parameter after applying, but preserve persona parameter
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('template'); // Remove only the template parameter
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, document.title, newUrl);
      } else {
        console.warn('TemplateContext: Template not found for ID:', templateId);
      }
    }
  }, [predefinedTemplates, applyTemplate]); // Run when templates are loaded

  // Save template function
  const saveTemplate = useCallback((newTemplate) => {
    setSavedTemplates(prev => {
      const updatedTemplates = [...prev, newTemplate];
      try {
        localStorage.setItem(SAVED_TEMPLATES_STORAGE_KEY, JSON.stringify(updatedTemplates));
        console.log('TemplateContext: Saved new template', newTemplate.name);
      } catch (error) {
        console.error("TemplateContext: Failed to save templates to localStorage:", error);
        // Optionally revert state update on error
        return prev;
      }
      return updatedTemplates;
    });
  }, []);

  // Delete template function
  const deleteTemplate = useCallback((templateId) => {
    setSavedTemplates(prev => {
      const updatedTemplates = prev.filter(t => t.id !== templateId);
      try {
        localStorage.setItem(SAVED_TEMPLATES_STORAGE_KEY, JSON.stringify(updatedTemplates));
        console.log('TemplateContext: Deleted template with ID:', templateId);
      } catch (error) {
        console.error("TemplateContext: Failed to update localStorage after deleting template:", error);
        // Optionally revert state update on error
        return prev;
      }
      return updatedTemplates;
    });
  }, []);

  // Combine predefined and saved templates for consumers
  const allTemplates = useMemo(() => [...predefinedTemplates, ...savedTemplates], [predefinedTemplates, savedTemplates]);

  // Context value
  const value = useMemo(() => ({
    predefinedTemplates,
    savedTemplates,
    allTemplates,
    saveTemplate,
    deleteTemplate,
    applyTemplate,
  }), [
    predefinedTemplates,
    savedTemplates,
    allTemplates,
    saveTemplate,
    deleteTemplate,
    applyTemplate,
  ]);

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};

// Custom hook to use the context
export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
}; 